/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMutation } from "@apollo/react-hooks";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import track, { useTracking } from "react-tracking";

import pulseIntroImage from "assets/pulse-intro.svg";
import {
  Button,
  Loading,
  ProgressBar,
  Question,
  StyledGrid,
  StyledP,
  TakePulseExitAffordance
} from "components/simple";
import { ExitContext } from "components/simple/TakePulseExitAffordance";
import {
  StartPulseTaking,
  SavePulseAnswers,
  GetPulseAvailability,
  GetLatestPulseScore,
  GetPulseScoreHistory
} from "graphql/pulse/pulse.gql";
import { GetSubHeaderData } from "graphql/user/user.gql";
import routesConfig from "utils/routesConfig";
import { color, spacingDefaults } from "style/constants";

const url = "/pulse/questions";

interface PulseAnswer {
  questionInternalName: string;
  answerData: any;
}

const PulseQuestions: React.FC = () => {
  const history = useHistory();
  const tracking = useTracking();

  // --- State ---
  const [takingId, setTakingId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1); // -1 = preamble
  const [answers, setAnswers] = useState<Record<any, any>[]>([]);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // --- Mutations ---
  const [startPulseTaking] = useMutation(StartPulseTaking);
  const [savePulseAnswers] = useMutation(SavePulseAnswers, {
    refetchQueries: [
      { query: GetPulseAvailability },
      { query: GetLatestPulseScore },
      { query: GetPulseScoreHistory },
      { query: GetSubHeaderData }
    ]
  });

  // --- Entry: call startPulseTaking once on mount ---
  useEffect(_initFlow, []);
  useEffect(_handleBeforeUnloadEvent, []);

  // --- Determine current question ---
  const currentQuestion = questions[currentQuestionIndex];

  // --- Loading state ---
  if (!takingId) {
    return <Loading />;
  }

  // --- Submission error state ---
  if (submissionError) {
    return (
      <StyledGrid
        alignItems="center"
        container
        direction="column"
        justifyContent="center"
        fillRemainingHeight
      >
        <StyledGrid item>
          <p>{submissionError}</p>
        </StyledGrid>
        <StyledGrid item>
          <Button onClick={() => _handleSubmit()}>Retry</Button>
        </StyledGrid>
      </StyledGrid>
    );
  }

  // --- Preamble screen (currentQuestionIndex === -1) ---
  if (currentQuestionIndex === -1) {
    return (
      <StyledGrid
        backgroundColor={color.WHITE}
        centerContent
        container
        direction="column"
        fillRemainingHeight
        itemMargin="2rem"
      >
        <StyledGrid item>
          <img
            alt="Pulse assessment introduction"
            src={pulseIntroImage}
            style={{ maxWidth: "100%" }}
          />
        </StyledGrid>
        <StyledGrid item>
          <StyledP textAlign="center" width="min(24rem, 90vw)">
            You will answer questions about your well-being as well as complete
            a word memory task and a creative thinking task. It takes about 15
            minutes.
          </StyledP>
        </StyledGrid>
        <StyledGrid item>
          <Button onClick={() => setCurrentQuestionIndex(0)} wide>
            Next
          </Button>
        </StyledGrid>
      </StyledGrid>
    );
  }

  // --- Question rendering ---
  return (
    <>
      <ProgressBar
        activeStep={currentQuestionIndex}
        steps={questions.length}
        variant="steps"
      />

      <StyledGrid
        container
        item
        marginLeft={spacingDefaults.medium}
        mobileMarginLeft="0"
        mobileTop="90px"
        position="fixed"
        top="120px"
        zIndex="1000"
      >
        <StyledGrid item marginLeft={spacingDefaults.xsmall}>
          <TakePulseExitAffordance
            context={_getExitContext()}
            onBack={_handleBack}
            onConfirmExit={_handleConfirmExit}
            onCancelExit={_handleCancelExit}
          />
        </StyledGrid>
      </StyledGrid>

      {currentQuestion && (
        <Question
          assessmentName="Pulse"
          question={currentQuestion}
          activeQuestion={currentQuestionIndex}
          answers={answers}
          updateAnswers={_updateAnswers}
        />
      )}
    </>
  );

  /* Internal */

  function _getExitContext(): ExitContext {
    // First question = first-screen (shows exit warning)
    if (currentQuestionIndex === 0) {
      return "first-screen";
    }
    // Timed questions and information screens both show the interruption warning
    const timedTypes = [
      "flash_words",
      "word_callback",
      "image_themes",
      "information"
    ];
    if (currentQuestion && timedTypes.includes(currentQuestion.questionType)) {
      return "timed-active";
    }
    // Default: can go back freely
    return "non-timed-not-first";
  }

  function _handleBack(): void {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }

  function _handleConfirmExit(context: ExitContext): void {
    tracking.trackEvent({
      url,
      actions: ["Pulse", "Exit Confirmed", context]
    });
    // Exit to pulse landing
    history.push(routesConfig.pulse.landing.path);
  }

  function _handleCancelExit(): void {
    // No-op — modal dismissed, user continues
  }

  function _initFlow(): void {
    (async function () {
      try {
        const { data } = await startPulseTaking();
        const response = data?.startPulseTaking;

        if (response) {
          setTakingId(response.takingId);

          const sortedQuestions = [...(response.assessment?.questions || [])];
          setQuestions(sortedQuestions);
        }

        tracking.trackEvent({
          url,
          actions: ["Pulse", "Start Pulse Taking"]
        });
      } catch (error) {
        setSubmissionError("Unable to start Pulse. Please try again.");
      }
    })();
  }

  function _updateAnswers(answerData: Record<any, any>): void {
    tracking.trackEvent({
      url,
      actions: ["Pulse", "Answer Question", currentQuestion.internalName]
    });

    const answer: PulseAnswer = {
      questionInternalName: currentQuestion.internalName,
      answerData
    };

    const updatedAnswers = cloneDeep(answers);
    updatedAnswers[currentQuestionIndex] = answer;
    setAnswers(updatedAnswers);

    // Move to next question or submit
    if (currentQuestionIndex === questions.length - 1) {
      _handleSubmit(updatedAnswers);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }

  function _handleSubmit(finalAnswers?: Record<any, any>[]): void {
    const answersToSubmit = finalAnswers || answers;
    setSubmissionError(null);

    (async function () {
      try {
        const pulseAnswers: PulseAnswer[] = answersToSubmit
          .filter(Boolean)
          .map((a: any) => ({
            questionInternalName: a.questionInternalName,
            answerData: a.answerData
          }));

        await savePulseAnswers({
          variables: {
            input: {
              takingId,
              answers: pulseAnswers
            }
          }
        });

        tracking.trackEvent({
          url,
          actions: ["Pulse", "Complete Pulse"]
        });

        history.push(routesConfig.pulse.complete.path);
      } catch (error) {
        setSubmissionError(
          "Something went wrong. Your answers are saved — tap Retry to try again."
        );
      }
    })();
  }

  /* Effects */

  function _handleBeforeUnloadEvent(): () => void {
    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Changes will be lost!";
    };

    return () => {
      window.onbeforeunload = null;
    };
  }
};

export default track({
  url
})(PulseQuestions);
